import { Create } from "@refinedev/antd";
import { useList, useNotification } from "@refinedev/core";
import { Alert, Checkbox, Form, Input, Select } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";

import { api } from "../api";
import type { InvitationAnswer, Role } from "../types";

type Values = {
  name: string;
  email: string;
  phoneNumber?: string;
  roleIds: string[];
  sendSms?: boolean;
};

/**
 * /team/new: adds a staff member with their roles and creates their invitation. The member's
 * page then shows the link once (and says whether it went by SMS).
 */
export function TeamNewPage() {
  const navigate = useNavigate();
  const { open } = useNotification();
  const [form] = Form.useForm<Values>();
  const [busy, setBusy] = useState(false);
  const { result: roles } = useList<Role>({ resource: "roles", pagination: { mode: "off" } });
  const phone = Form.useWatch("phoneNumber", form);

  async function save(values: Values) {
    setBusy(true);
    try {
      const answer = await api<InvitationAnswer & { id: string }>("/api/office/staff", {
        method: "POST",
        body: {
          ...values,
          phoneNumber: values.phoneNumber ?? "",
          sendSms: Boolean(values.sendSms),
        },
      });
      navigate(`/team/${answer.id}`, {
        state: { invitationUrl: answer.invitationUrl, smsSent: answer.smsSent },
      });
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Create
      title="Ekip üyesi ekle"
      saveButtonProps={{
        onClick: () => form.submit(),
        loading: busy,
        children: "Ekle ve davet et",
      }}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={save}
        style={{ maxWidth: 520 }}
      >
        <Form.Item
          label="Ad Soyad"
          name="name"
          rules={[{ required: true, min: 2, message: "Ad soyad gir." }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="E-posta"
          name="email"
          extra="Giriş adı olur; sonradan değiştirilemez."
          rules={[{ required: true, type: "email", message: "Geçerli bir e-posta adresi gir." }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Cep telefonu"
          name="phoneNumber"
          extra="İsteğe bağlı. Davet SMS ile de gidebilir."
        >
          <Input inputMode="tel" placeholder="5XX XXX XX XX" />
        </Form.Item>
        <Form.Item
          label="Roller"
          name="roleIds"
          rules={[{ required: true, message: "En az bir rol seç." }]}
        >
          <Select
            mode="multiple"
            optionFilterProp="label"
            placeholder="Rol seç"
            options={roles.data.map((role) => ({ value: role.id, label: role.name }))}
          />
        </Form.Item>
        <Form.Item name="sendSms" valuePropName="checked">
          <Checkbox disabled={!phone?.trim()}>Davet bağlantısını SMS ile gönder</Checkbox>
        </Form.Item>
        <Alert
          type="info"
          showIcon
          message="Davet bağlantısı 72 saat geçerlidir. Ekip üyesi şifresini belirler, sonra iki adımlı doğrulamayı kurar."
        />
      </Form>
    </Create>
  );
}
